import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, ShieldCheck, Sparkles, Handshake, Phone, Mail, ArrowRight, ClipboardList, Users, TrendingUp, Trophy } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { usePageSeo } from "../lib/seo";
import { ENTITY_SITE, brokerageNode, agentNode, breadcrumbNode } from "../lib/entityGraph";

const SITE = "https://equitashomes.com";
const CANON = `${ENTITY_SITE}/about`;

const CREDS_LEFT = [
  { icon: ClipboardList, text: "California DRE #01151598" },
  { icon: ShieldCheck, text: "Former San Jose Fire Department Deputy Fire Marshal" },
  { icon: Users, text: "25 Years of Public Service" },
];
const CREDS_RIGHT = [
  { icon: TrendingUp, text: "6 Years with a Top 1% Silicon Valley Real Estate Team" },
  { icon: Trophy, text: "$1B+ Team Career Sales" },
];

const PRINCIPLES = [
  { icon: Scale, title: "Fairness", body: "Homeowners deserve exceptional representation at a fair price. What you pay should make sense for the service, expertise, and value you receive." },
  { icon: ShieldCheck, title: "Equity", body: "Your home is often one of your largest financial assets. Our job is not only to help you achieve a strong sale price, but to help protect the equity you've spent years building." },
  { icon: Handshake, title: "Balance", body: "Exceptional service and reasonable fees shouldn't be mutually exclusive. We combine experienced representation, professional marketing, modern technology, and an efficient business model to deliver both." },
  { icon: Sparkles, title: "Integrity", body: "Straightforward advice. Transparent pricing. No gimmicks and no hidden fees. We believe our responsibility is simple: represent our clients' interests and help them make informed real estate decisions." },
];

const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

export default function About() {
  usePageSeo({
    title: "About Louis Fong & Equitas Homes | 1% Listing Broker, San Jose",
    description: "Meet Louis Fong — founder & Broker of Equitas Homes, a full-service 1% listing brokerage in San Jose & Santa Clara County helping sellers keep more home equity.",
    canonical: CANON,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        brokerageNode({ founder: { "@id": `${ENTITY_SITE}/#agent` }, foundingLocation: { "@type": "Place", name: "San Jose, California" } }),
        agentNode({
          image: `${SITE}/louis-fong.jpg`,
          alumniOf: { "@type": "Organization", name: "San Jose Fire Department" },
          founderOf: { "@id": `${ENTITY_SITE}/#brokerage` },
        }),
        breadcrumbNode(`${CANON}#breadcrumb`, [
          { name: "Home", item: `${ENTITY_SITE}/` },
          { name: "About", item: CANON },
        ]),
        { "@type": "AboutPage", "@id": `${CANON}#webpage`, url: CANON, name: "About Equitas Homes", mainEntity: { "@id": `${ENTITY_SITE}/#agent` } },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-brand-cream paper-grain flex flex-col" data-testid="about-page">
      <Header />
      <main className="flex-1">
        {/* HERO */}
        <section className="bg-brand-emerald text-brand-cream">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 lg:py-28">
            <motion.div {...fade}>
              <span className="eyebrow">A New Way to Real Estate</span>
              <h1 className="mt-5 font-serif font-light leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
                It's not just what you sell for.<br /><span className="text-brand-gold">It's what you keep.</span>
              </h1>
              <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-brand-cream/75">
                While most real estate companies talk about how much they sell, we focus on something more important—how much equity our clients keep.
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 1 — MEET LOUIS FONG */}
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24" data-testid="about-louis">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-14 items-center">
            <motion.div {...fade}>
              <div className="overflow-hidden rounded-2xl border border-brand-hairline shadow-[0_18px_50px_rgba(27,59,43,0.12)]">
                <img src="/louis-fong.jpg" width="427" height="534" loading="eager"
                  alt="Louis Fong, Founder and Broker of Equitas Homes, a 1% listing brokerage in San Jose, Santa Clara County"
                  className="w-full aspect-[4/5] object-cover object-[center_18%]" data-testid="about-louis-photo" />
              </div>
            </motion.div>

            <motion.div {...fade}>
              <span className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold">— Meet</span>
              <h2 className="mt-1 font-serif font-light leading-[0.95] text-5xl sm:text-6xl text-brand-ink">Louis Fong</h2>
              <p className="mt-3 text-sm uppercase tracking-[0.22em] text-brand-stone font-medium">Founder &amp; Broker</p>

              <div className="mt-6 space-y-3.5 text-brand-stone leading-relaxed text-[0.95rem]">
                <p>After a 25-year career serving Silicon Valley with the San Jose Fire Department—rising from firefighter and paramedic to Deputy Fire Marshal—I brought that same commitment to service, preparation, and accountability to real estate.</p>
                <p>Before founding Equitas Homes, I spent six years working alongside a Top 1% Silicon Valley real estate team with more than $1 billion in career sales.</p>
                <p>That experience also showed me how much the real estate industry has changed. Technology, automation, AI, and modern marketing have made it possible to operate a highly efficient brokerage without the traditional overhead that homeowners have historically paid for through higher commissions.</p>
                <p>I founded Equitas Homes around a simple idea: <strong className="text-brand-ink font-semibold">use technology to reduce the cost</strong> of delivering exceptional real estate service—not the quality of the service itself.</p>
                <p>The result is full-service representation, professional marketing, experienced negotiation, and a <Link to="/sell/san-jose" className="text-brand-emerald font-semibold hover:underline">1% listing fee</Link> designed to help homeowners keep more of their equity.</p>
              </div>

              <div className="mt-7 pt-7 border-t border-brand-hairline grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
                {[...CREDS_LEFT, ...CREDS_RIGHT].map((c) => (
                  <div key={c.text} className="flex items-start gap-3 text-sm text-brand-ink">
                    <c.icon className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" strokeWidth={1.75} /> {c.text}
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href="tel:+14085611321" data-testid="about-phone" className="inline-flex items-center justify-center gap-2.5 rounded-xl border border-brand-hairline bg-white px-5 py-3 text-sm font-medium text-brand-ink hover:border-brand-emerald/50 transition-colors"><Phone className="h-4 w-4 text-brand-gold" /> (408) 561-1321</a>
                <a href="mailto:louis@equitashomes.com" data-testid="about-email" className="inline-flex items-center justify-center gap-2.5 rounded-xl border border-brand-hairline bg-white px-5 py-3 text-sm font-medium text-brand-ink hover:border-brand-emerald/50 transition-colors"><Mail className="h-4 w-4 text-brand-gold" /> louis@equitashomes.com</a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — WHAT EQUITAS MEANS */}
        <section className="bg-brand-sand/60 border-y border-brand-hairline">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 lg:py-28">
            <motion.div {...fade} className="max-w-3xl">
              <h2 className="font-serif text-3xl sm:text-4xl text-brand-ink">What Equitas Means</h2>
              <p className="mt-4 text-brand-stone leading-relaxed">The name Equitas is rooted in the principles of fairness, equity, balance, and doing what is right. Those ideas aren't simply part of our name. They're the foundation of how we believe real estate should work.</p>
            </motion.div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {PRINCIPLES.map((p, i) => (
                <motion.div key={p.title} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl bg-white border border-brand-hairline p-6 hover:border-brand-emerald/50 transition-colors" data-testid="about-principle-card">
                  <div className="h-11 w-11 rounded-xl bg-brand-emerald/10 flex items-center justify-center"><p.icon className="h-5 w-5 text-brand-emerald" /></div>
                  <h3 className="mt-4 font-serif text-xl text-brand-ink">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-stone leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 — OUR PHILOSOPHY */}
        <section className="mx-auto max-w-4xl px-5 sm:px-8 py-24 lg:py-32 text-center">
          <motion.div {...fade}>
            <span className="eyebrow">Our Philosophy</span>
            <blockquote className="mt-6 font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.25] text-brand-ink">
              “We created Equitas Homes around a simple belief: homeowners shouldn't have to choose between exceptional real estate service and keeping more of their equity.”
            </blockquote>
            <div className="mt-10 space-y-5 text-brand-stone leading-relaxed text-left max-w-2xl mx-auto">
              <p>Technology has changed nearly every part of the real estate transaction. We believe homeowners should benefit from those efficiencies too.</p>
              <p>That's why Equitas Homes combines experienced representation, professional marketing, modern technology, AI-powered workflows, and a lean brokerage model to provide full-service representation at a more reasonable cost.</p>
            </div>
            <p className="mt-12 font-serif text-xl sm:text-2xl text-brand-emerald leading-snug">
              Better technology. Lower overhead. Full service.<br /><span className="text-brand-gold">More equity stays with you.</span>
            </p>
          </motion.div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-brand-dark text-brand-cream">
          <div className="mx-auto max-w-5xl px-5 sm:px-8 py-20 lg:py-24 text-center">
            <motion.div {...fade}>
              <span className="eyebrow text-brand-gold">See the Difference</span>
              <h2 className="mt-4 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Discover what a 1% listing fee could keep in your pocket.
              </h2>
              <p className="mt-5 max-w-2xl mx-auto text-brand-cream/70 leading-relaxed">
                See your estimated home value, selling costs, and potential net proceeds with our Santa Clara County Home Value &amp; Net Equity Calculator.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/" data-testid="about-cta-primary"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-cream px-7 py-3.5 text-sm font-semibold text-brand-emerald hover:bg-white transition-colors">
                  Calculate Your True Net Equity <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/sell/san-jose" data-testid="about-cta-secondary"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-cream/30 px-7 py-3.5 text-sm font-medium text-brand-cream hover:bg-brand-cream/10 transition-colors">
                  Learn How Our 1% Listing Service Works
                </Link>
              </div>
              <p className="mt-6 text-sm text-brand-cream/60">
                Prefer to talk first? <Link to="/contact" className="text-brand-gold hover:underline" data-testid="about-contact-link">Contact Louis Fong</Link>.
              </p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
